import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo, useState } from 'react';
import { Badge, HelpHint, InlineNotice, SectionCard } from '@wit-support/ui';
import { ZodField, useZodFormContext } from '@wit-support/ui-forms';
import { CircleAlert, Info, Lock, TriangleAlert } from 'lucide-react';
import { useRegistryData, widgetForType } from './field-widget.js';
import { FixedFieldDetails, hasFixedDetails } from './structured-widgets.js';
import { NON_FORM_TYPES, fieldName, fromFormValues, isVisible, widgetSchemaFor, } from './to-zod.js';
import { GROUP_LABEL_RU, GROUP_ORDER, sortDescs } from './use-registry.js';
import { issueMessageText } from '../issues.js';
import { displayFixed, displayTitle, displayValue } from './value-display.js';
/** Проблемы значения одного поля (из preview / 400 `invalid-values`) — под полем. */
export function FieldIssues({ issues }) {
    if (issues.length === 0)
        return null;
    return (_jsx("ul", { className: "flex flex-col gap-1", "data-testid": "field-issues", children: issues.map((i, idx) => (_jsx("li", { children: _jsx(InlineNotice, { variant: i.severity === 'error' ? 'danger' : i.severity === 'info' ? 'muted' : 'warning', icon: i.severity === 'error' ? CircleAlert : i.severity === 'info' ? Info : TriangleAlert, as: "span", children: issueMessageText(i.message) }) }, `${i.code}-${idx}`))) }));
}
/** Проблемы, сгруппированные по `fieldKey` (`null` — общие, без поля). */
export function issuesByField(issues) {
    const m = new Map();
    for (const i of issues ?? []) {
        const list = m.get(i.fieldKey) ?? [];
        list.push(i);
        m.set(i.fieldKey, list);
    }
    return m;
}
/**
 * Замечания под полями — пока поле не тронули: замечание по `fieldKey`
 * пропадает, как только значение поля отличается от того, что было, когда
 * замечания пришли (а не держится до следующего «Сохранить»/«Проверить»).
 * Вернули прежнее значение — замечание снова видно: оно про это значение.
 * Замечания без поля (`fieldKey: null`) и по креативам (`itemIndex`) — как есть.
 *
 * `current` — значения по ключам контракта (`adset.bidAmount`, …).
 */
export function useIssuesUntilEdited(issues, current) {
    const list = issues ?? [];
    const key = JSON.stringify(list);
    // снимок значений на момент прихода замечаний (строкой: значения формы
    // react-hook-form меняет на месте, ссылка на объект снимком не годится)
    const [seen, setSeen] = useState(() => ({ key, values: snapshotOf(list, current) }));
    let base = seen;
    if (seen.key !== key) {
        base = { key, values: snapshotOf(list, current) };
        setSeen(base);
    }
    return list.filter((i) => i.fieldKey == null ||
        i.itemIndex != null ||
        base.values.get(i.fieldKey) === JSON.stringify(current[i.fieldKey] ?? null));
}
function snapshotOf(issues, current) {
    const m = new Map();
    for (const i of issues) {
        if (i.fieldKey != null)
            m.set(i.fieldKey, JSON.stringify(current[i.fieldKey] ?? null));
    }
    return m;
}
/**
 * Поля реестра внутри текущего `ZodForm`. Ничего не знает о конкретных
 * ключах: подпись, подсказка, виджет, опции и видимость — из дескриптора.
 * Новое поле реестра с уже знакомым `type` появляется здесь само.
 */
export function RegistryFields({ descriptors, prefix, issues, contextValues, renderExtra, grouped = false, groupLabel, insertAfter, disabled, disabledKeys, }) {
    const { form } = useZodFormContext();
    const { currency } = useRegistryData();
    const formDescs = useMemo(() => sortDescs(descriptors.filter((d) => !NON_FORM_TYPES.has(d.type))), [descriptors]);
    const watched = form.watch(prefix);
    const current = { ...(contextValues ?? {}), ...fromFormValues(formDescs, watched) };
    const byField = issuesByField(useIssuesUntilEdited(issues, current));
    const renderField = (d) => {
        if (!isVisible(d, current))
            return null;
        const As = widgetForType(d.type);
        const own = byField.get(d.key) ?? [];
        return (_jsxs("div", { className: "flex flex-col gap-2", "data-field-key": d.key, children: [_jsx(ZodField, { name: `${prefix}.${fieldName(d.key)}`, schema: widgetSchemaFor(d, currency), ...(As ? { as: As } : {}), ...(disabled || disabledKeys?.has(d.key) ? { disabled: true } : {}) }), _jsx(FieldIssues, { issues: own }), renderExtra ? renderExtra(d) : null] }, d.key));
    };
    if (!grouped)
        return _jsx("div", { className: "flex flex-col gap-4", children: formDescs.map(renderField) });
    const labelOf = (g) => groupLabel?.(g) ?? GROUP_LABEL_RU[g];
    return (_jsx("div", { className: "flex flex-col gap-4", children: GROUP_ORDER.map((g) => {
            const inGroup = formDescs.filter((d) => d.group === g);
            const after = insertAfter?.group === g ? insertAfter.node : null;
            if (inGroup.length === 0)
                return after ? _jsx(Fragment, { children: after }, g) : null;
            const fields = _jsx("div", { className: "flex flex-col gap-4", children: inGroup.map(renderField) });
            return (_jsxs(Fragment, { children: [grouped === 'headings' ? (_jsxs("section", { className: "flex flex-col gap-3", "data-testid": `fields-group-${g}`, children: [_jsx("h3", { className: "text-sm font-semibold text-[var(--color-text-primary)]", children: labelOf(g) }), fields] })) : (_jsx(SectionCard, { title: labelOf(g), children: fields })), after] }, g));
        }) }));
}
/**
 * Контекст подписей значений из справочников раздела. Валюта — явная, иначе
 * валюта экрана из `RegistryDataProvider`.
 */
export function useDisplayContext(currency) {
    const { registry, locales, countryLists, currency: screenCurrency } = useRegistryData();
    return {
        // пресеты — из реестра, названия языков — из справочника сервиса
        ...(registry ? { localePresets: registry.localePresets } : {}),
        ...(locales ? { locales: locales.items } : {}),
        ...(countryLists ? { countryLists } : {}),
        ...(currency !== undefined
            ? { currency }
            : screenCurrency != null
                ? { currency: screenCurrency }
                : {}),
    };
}
/** Чипы «Подпись: значение» — значения шаблона только для чтения (экран запуска). */
export function ValueChips({ descriptors, values, currency, }) {
    const ctx = useDisplayContext(currency);
    const visible = sortDescs(descriptors).filter((d) => !NON_FORM_TYPES.has(d.type) && isVisible(d, values) && d.key in values);
    if (visible.length === 0) {
        return _jsx("span", { className: "text-sm text-[var(--color-text-muted)]", children: "\u041D\u0435\u0442 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0439" });
    }
    return (_jsx("div", { className: "flex flex-wrap gap-1.5", "data-testid": "value-chips", children: visible.map((d) => (_jsxs(Badge, { variant: "neutral", size: "md", title: displayTitle(d, values[d.key], ctx) ?? d.hint ?? undefined, children: [_jsxs("span", { className: "text-[var(--color-text-muted)]", children: [d.label, ":"] }), displayValue(d, values[d.key], ctx)] }, d.key))) }));
}
/** «Зафиксировано системой» — поля `fixed` с объяснением, только чтение. */
export function FixedFieldsList({ descriptors }) {
    const ctx = useDisplayContext();
    const fixed = sortDescs(descriptors.filter((d) => d.mode === 'fixed'));
    if (fixed.length === 0)
        return null;
    return (_jsx("ul", { className: "flex flex-col divide-y divide-[var(--color-border-subtle)]", "data-testid": "fixed-fields", children: fixed.map((d) => (_jsxs("li", { className: "flex flex-wrap items-center gap-2 py-2 text-sm", children: [_jsx(Lock, { "aria-hidden": true, className: "h-3.5 w-3.5 text-[var(--color-text-muted)]" }), _jsx("span", { className: "font-medium", children: d.label }), _jsx("span", { className: "text-[var(--color-text-secondary)]", title: d.fixedDisplay != null && d.fixedDisplay !== ''
                        ? undefined
                        : displayTitle(d, d.fixedValue, ctx), children: displayFixed(d, ctx) }), d.verify != null ? (_jsx(Badge, { variant: d.verify.severity === 'error' ? 'danger' : 'warning', children: d.verify.severity === 'error' ? 'сверка: блокирует' : 'сверка: предупреждение' })) : null, d.hint || hasFixedDetails(d) ? (_jsxs(HelpHint, { label: `Почему «${d.label}» зафиксировано`, children: [d.hint, _jsx(FixedFieldDetails, { desc: d })] })) : null] }, d.key))) }));
}
//# sourceMappingURL=registry-form.js.map